import React from 'react';
import {
  Checkbox,
  Col,
  Form,
  FormModal,
  Input,
  Row,
  Select,
  TagSelect,
  Typography,
  Upload,
  UploadContent,
} from 'components';
import { ValidationErrors } from 'constants/validations';
import { useClientsDetailsEdit } from './useClientsDetailsEdit';
import { webSiteUrlRegex } from 'utils/helpers';

const { Item } = Form;
const { TextArea } = Input;

export interface IClientDetailsUpdate {
  onCancel: () => void;
  isOpen?: boolean;
}

export const ClientDetailsUpdate = React.memo<IClientDetailsUpdate>((props) => {
  const { onCancel, isOpen } = props;
  const [form] = Form.useForm();

  const {
    SELECT_TYPE_MAPPING,
    onFinish,
    contextHolder,
    handleFileSizeValidation,
    isSaveBtnDisabled,
    tagData,
    isLoading,
    priorityLevel,
  } = useClientsDetailsEdit({
    onCancel,
    isOpen,
    form,
  });

  return (
    <>
      {contextHolder}
      <FormModal
        disableOkBtn={isSaveBtnDisabled}
        title={'Update Client Details'}
        okText={'Save'}
        open={isOpen}
        isLoading={isLoading}
        form={form}
        onSubmit={onFinish}
        onCancel={() => {
          form.resetFields();
          onCancel();
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={'Client Name*'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Client Name'),
                },
              ]}
            >
              <Input size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Item
              label={'Client Logo'}
              name="logo"
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList || []}
            >
              <Upload.Dragger beforeUpload={handleFileSizeValidation} accept={'.jpg, .jpeg, .png'} maxCount={1}>
                <UploadContent
                  title={'Upload or Drag and Drop IMG'}
                  message={'Max size: 5MB / Format: JPG, JPEG, PNG'}
                />
              </Upload.Dragger>
            </Item>
          </Col>
          <Col span={24}>
            <Form.Item name="verified" valuePropName={'checked'}>
              <Checkbox>
                <Typography.Text style={{ color: '#05796b' }}>Set as verified</Typography.Text>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="webSiteUrl"
              label={'Client Website'}
              rules={[{ pattern: webSiteUrlRegex, message: 'Please enter a valid website URL.' }]}
            >
              <Input size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={'clientTypeId'}
              label={'Client Type*'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Client Type'),
                },
              ]}
            >
              <Select
                size={'large'}
                style={{ width: '100%' }}
                options={SELECT_TYPE_MAPPING.client_type}
                showSearch
                filterOption={(input, option) => {
                  if (option) {
                    const label: string = option.label?.toString() || '';
                    return label?.toLowerCase()?.includes(input?.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="countryId" label={'Main Country'}>
              <Select
                options={SELECT_TYPE_MAPPING.main_country}
                size={'large'}
                showSearch
                filterOption={(input, option) => {
                  if (option) {
                    const label: string = option.label?.toString() || '';
                    return label?.toLowerCase()?.includes(input?.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="languageId" label={'Main Language'}>
              <Select
                options={SELECT_TYPE_MAPPING.main_language}
                size={'large'}
                showSearch
                filterOption={(input, option) => {
                  if (option) {
                    const label: string = option.label?.toString() || '';
                    return label?.toLowerCase()?.includes(input?.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="priorityLevel" label={'Priority Level'}>
              <Select options={SELECT_TYPE_MAPPING.priority_level} size={'large'} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="priorityRationale"
              label={'Priority Rationale'}
              rules={[
                {
                  required: priorityLevel !== 'STANDARD',
                  message: 'Priority rationale is required when Priority level is High or Low.',
                },
              ]}
            >
              <Input.TextArea rows={3} size={'large'} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Item label={'Additional Notes'} name={'additionalNotes'}>
              <TextArea rows={4} />
            </Item>
          </Col>
          <Col span={24}>
            <TagSelect data={tagData} name={'manualTags'} label={'Tags'} />
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
